import React, { ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface PermissionGateRenderProps {
    allowed: boolean;
}

type PermissionGateChildren =
    | ReactNode
    | ((props: PermissionGateRenderProps) => ReactNode);

interface PermissionGateProps {
    module: string;
    action: string;
    fallback?: ReactNode;
    children: PermissionGateChildren;
}

/**
 * Conditionally renders children based on whether the current user
 * can perform the specified action on the given module.
 *
 * Supports two forms:
 *  - Wrapper: <PermissionGate module="x" action="y">content</PermissionGate>
 *  - Render-prop: <PermissionGate module="x" action="y">{({ allowed }) => ...}</PermissionGate>
 */
const PermissionGate: React.FC<PermissionGateProps> = ({
    module,
    action,
    fallback,
    children,
}) => {
    const { canPerformAction } = useAuth();
    const allowed = canPerformAction(module, action);

    // Render-prop form
    if (typeof children === 'function') {
        return <>{children({ allowed })}</>;
    }

    // Wrapper form
    if (allowed) {
        return <>{children}</>;
    }

    return fallback ? <>{fallback}</> : null;
};

export default PermissionGate;
